import React from 'react';
import { useNavigate } from 'react-router-dom';

interface NotAuthenticatedProps {
  /** Title of the message */
  title?: string;
  /** Main message content */
  message?: string | React.ReactNode;
  /** Text for the action button */
  buttonText?: string;
  /** Link to navigate to on button click (if onButtonClick is not provided) */
  buttonLink?: string;
  /** Callback function for button click */
  onButtonClick?: () => void;
  /** Optional custom icon */
  icon?: React.ReactNode;
  /** Tailwind classes for the icon background container */
  iconBgClassName?: string;
  /** Tailwind classes for the icon itself (if using default icon) */
  iconClassName?: string;
  /** Tailwind classes for the action button */
  buttonClassName?: string;
  /** Tailwind classes for the root container */
  className?: string;
}

const NotAuthenticated: React.FC<NotAuthenticatedProps> = ({
  title = 'Authentication Required',
  message = 'Please log in to access this page.',
  buttonText = 'Go to Login',
  buttonLink = '/',
  onButtonClick,
  icon,
  iconBgClassName = 'bg-gray-300',
  iconClassName = 'text-gray-600',
  buttonClassName = 'bg-black text-white hover:bg-gray-800',
  className = 'flex items-center justify-center min-h-[400px]',
}) => {
  const navigate = useNavigate();

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      navigate(buttonLink);
    }
  };

  return (
    <div className={className}>
      <div className="text-center max-w-md mx-auto">
        <div
          className={`w-16 h-16 ${iconBgClassName} rounded-full flex items-center justify-center mx-auto mb-6`}
        >
          {icon || (
            <svg
              className={`w-8 h-8 ${iconClassName}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          )}
        </div>

        <h3 className="text-xl font-baijamjuree font-bold text-gray-900 mb-3">
          {title}
        </h3>

        {message && (
          <div className="text-gray-700 mb-6 font-baijamjuree max-w-md mx-auto">
            {message}
          </div>
        )}

        <button
          onClick={handleButtonClick}
          className={`${buttonClassName} px-6 py-2 rounded-full text-sm font-semibold font-baijamjuree uppercase tracking-wide transition-colors`}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

export default NotAuthenticated;
